import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import axios from 'axios';
import socket from '../../utils/socket';
import { usePermissions } from '../../context/PermissionsContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';

const Sidebar = ({ workspaceUuid, collapsed, onToggle, mobileOpen }) => {
  const location = useLocation();
  const { can, role: permRole, readOnly } = usePermissions();
  const chatsRoot = `/workspace/${workspaceUuid}/chats`;
  const isChatsActive = location.pathname.startsWith(chatsRoot);
  const [chatsExpanded, setChatsExpanded] = useState(isChatsActive);

  useEffect(() => {
    if (isChatsActive) setChatsExpanded(true);
  }, [isChatsActive]);

  const chatSubItems = [
    { name: 'WhatsApp', platform: 'whatsapp', path: `${chatsRoot}/whatsapp` },
    { name: 'Instagram', platform: 'instagram', path: `${chatsRoot}/instagram` },
    { name: 'Messenger', platform: 'facebook', path: `${chatsRoot}/facebook` },
    { name: 'Website', platform: 'website', path: `${chatsRoot}/website` },
    { name: 'Comments', platform: 'comments', path: `${chatsRoot}/comments` }
  ];

  const mainNavAll = [
    { name: 'Dashboard', page: 'dashboard', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
    ), path: `/workspace/${workspaceUuid}/dashboard` },
    { name: 'Notifications', page: 'notifications', badge: 'unreadNotifications', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" /><path d="M13.73 21a2 2 0 0 1-3.46 0" /></svg>
    ), path: `/workspace/${workspaceUuid}/notifications` },
    { name: 'Tickets', page: 'tickets', badge: 'unreadTickets', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M15 5h2a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2" /><rect x="9" y="3" width="6" height="4" rx="1" /><path d="M9 14h6" /><path d="M9 10h6" /></svg>
    ), path: `/workspace/${workspaceUuid}/tickets` },
    { name: 'Templates', page: 'templates', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /><polyline points="10 9 9 9 8 9" /></svg>
    ), path: `/workspace/${workspaceUuid}/templates/whatsapp` },
    { name: 'Team', page: 'team', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>
    ), path: `/workspace/${workspaceUuid}/users` },
  ];
  const mainNav = mainNavAll.filter(i => can(i.page));

  const toolsNavAll = [
    { name: 'Automations', page: 'automations', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12" /></svg>
    ), path: `/workspace/${workspaceUuid}/automations` },
    { name: 'Integrations', page: 'integrations', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M20 7h-9m3 3L11 7l3-3m-6 10h9m-3 3l3-3-3-3" /><rect x="3" y="5" width="6" height="6" rx="1" /><rect x="15" y="13" width="6" height="6" rx="1" /></svg>
    ), path: `/workspace/${workspaceUuid}/integrations` },
    { name: 'Orders', page: 'orders', badge: 'unreadOrders', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
    ), path: `/workspace/${workspaceUuid}/orders` },
    { name: 'Campaigns', page: 'campaigns', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M3 11l18-8-8 18-2-8-8-2z" /></svg>
    ), path: `/workspace/${workspaceUuid}/campaigns` },
    { name: 'Shopify App', page: 'integrations', icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z" /><line x1="3" y1="6" x2="21" y2="6" /><path d="M16 10a4 4 0 0 1-8 0" /></svg>
    ), path: `/workspace/${workspaceUuid}/shopify-app` },
    { name: 'Intent Tags', adminOnly: true, icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M7 7h10v10H7z" /><path d="M7 7l5 5-5 5M17 7l-5 5 5 5" /></svg>
    ), path: `/workspace/${workspaceUuid}/tags` },
    { name: 'AI Agents', adminOnly: true, icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><circle cx="12" cy="12" r="9" /><circle cx="9" cy="10" r="1" fill="currentColor" /><circle cx="15" cy="10" r="1" fill="currentColor" /><path d="M9 15c1 1 5 1 6 0" /></svg>
    ), path: `/workspace/${workspaceUuid}/ai-agents` },
  ];
  // Demo (read-only) accounts also see the admin-only tools (AI Agents, Intent
  // Tags) so the demo showcases them; writes are still blocked server-side.
  const toolsNav = toolsNavAll.filter(i => i.adminOnly ? (permRole === 'Admin' || readOnly) : can(i.page));

  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadTickets, setUnreadTickets] = useState(0);
  const [unreadOrders, setUnreadOrders] = useState(0);
  const [chatUnread, setChatUnread] = useState({}); // platform -> combined unread
  const user = JSON.parse(localStorage.getItem('user') || '{"name": "Guest", "email": ""}');
  const workspaceName = localStorage.getItem('workspaceName') || 'Workspace';
  const role = localStorage.getItem('userRole') || 'User';

  const fetchUnread = useCallback(async () => {
    if (!workspaceUuid) return;
    try {
      const [notifRes, ticketRes, chatRes, cmtRes, orderRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications?workspaceUuid=${workspaceUuid}`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/tickets?workspaceUuid=${workspaceUuid}`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/workspaces/${workspaceUuid}/chat-unread`).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/integrations/instagram/comments-unread?workspaceUuid=${workspaceUuid}`).catch(() => ({ data: {} })),
        can('orders') ? axios.get(`${API_URL}/api/wip/orders/unread-count?workspaceUuid=${workspaceUuid}`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }).catch(() => ({ data: {} })) : Promise.resolve({ data: {} }),
      ]);
      setUnreadNotifications(notifRes.data.unreadCount || 0);
      setUnreadTickets(ticketRes.data.unreadCount || 0);
      setUnreadOrders(orderRes.data.unreadCount || 0);
      setChatUnread({ ...(chatRes.data?.byPlatform || {}), comments: cmtRes.data?.total || 0 });
    } catch (err) {}
  }, [workspaceUuid, can]);

  // Real-time: refetch counts whenever any chat / notification / ticket event
  // fires for this workspace (every workspace user's sidebar updates live).
  useEffect(() => {
    if (!workspaceUuid) return;
    socket.emit('joinWorkspace', workspaceUuid);
    fetchUnread();
    const onEvt = () => fetchUnread();
    socket.on('newMessage', onEvt);
    socket.on('updateConversation', onEvt);
    socket.on('notificationCreated', onEvt);
    socket.on('ticketCreated', onEvt);
    socket.on('countsChanged', onEvt);
    socket.on('wip:created', onEvt);   // new Shopify order row -> bump Orders badge
    const interval = setInterval(fetchUnread, 20000); // safety net
    return () => {
      socket.off('newMessage', onEvt);
      socket.off('updateConversation', onEvt);
      socket.off('notificationCreated', onEvt);
      socket.off('ticketCreated', onEvt);
      socket.off('countsChanged', onEvt);
      socket.off('wip:created', onEvt);
      clearInterval(interval);
    };
  }, [workspaceUuid, fetchUnread]);

  // Refetch when navigating (so opening Notifications/Tickets zeroes their badge).
  useEffect(() => { fetchUnread(); }, [location.pathname, fetchUnread]);

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/login';
  };

  return (
    <aside className={`premium-sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="ps-logo-section">
        <div className="ps-logo-box">
          <div className="ps-logo-icon">
            <img src="/vetobase-icon.png" alt="Vetobase" />
          </div>
          <div className="ps-logo-wrapper" style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', minWidth: 0, lineHeight: 1.15 }}>
            <div className="ps-logo-text" style={{ lineHeight: 1.15 }}>Vetobase</div>
            {!collapsed && <div style={{ fontSize: '10px', color: '#9C978C', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.12em', marginTop: '2px', fontFamily: "'IBM Plex Mono', monospace", whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{workspaceName}</div>}
          </div>
        </div>
        <button className="ps-collapse-btn" onClick={onToggle} title={collapsed ? "Expand Sidebar" : "Collapse Sidebar"}>
          {collapsed ? '›' : '‹'}
        </button>
      </div>

      <div className="ps-nav-list">
        <div className="ps-nav-group">
          {!collapsed && <div className="ps-nav-label">Main Menu</div>}
          {mainNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.path}
              className={({ isActive }) => `ps-nav-item ${isActive ? 'active' : ''}`}
            >
              <span style={{ display: 'flex' }} className="ps-nav-icon">{item.icon}</span>
              <span style={{ flex: 1 }}>{item.name}</span>
              {item.badge === 'unreadNotifications' && unreadNotifications > 0 && !collapsed && (
                <span style={{ background: '#F6E4E1', color: '#B23A3A', fontSize: '11px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '12px' }}>
                  {unreadNotifications}
                </span>
              )}
              {item.badge === 'unreadTickets' && unreadTickets > 0 && !collapsed && (
                <span style={{ background: '#F6E4E1', color: '#B23A3A', fontSize: '11px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '12px' }}>
                  {unreadTickets}
                </span>
              )}
            </NavLink>
          ))}

          {/* Chats — expandable group (only when the user can access chats) */}
          {can('chats') && (<>
          <div
            className={`ps-nav-item ${isChatsActive ? 'active' : ''}`}
            onClick={() => setChatsExpanded(v => !v)}
            style={{ cursor: 'pointer', userSelect: 'none' }}
          >
            <span style={{ display: 'flex' }} className="ps-nav-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" /></svg>
            </span>
            <span style={{ flex: 1 }}>Chats</span>
            {!collapsed && (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transform: chatsExpanded ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s', opacity: 0.6 }}>
                <polyline points="9 18 15 12 9 6" />
              </svg>
            )}
          </div>
          {chatsExpanded && !collapsed && (
            <div style={{ marginLeft: '34px', display: 'flex', flexDirection: 'column', gap: '2px', marginTop: '2px', marginBottom: '4px' }}>
              {chatSubItems.map(sub => {
                const subActive = location.pathname.startsWith(sub.path);
                const unread = chatUnread[sub.platform] || 0;
                return (
                  <NavLink
                    key={sub.name}
                    to={sub.path}
                    className="ps-nav-item ps-subnav"
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '7px 13px',
                      fontSize: '12.5px',
                      fontWeight: subActive ? 700 : 500,
                      transform: 'none'
                    }}
                  >
                    <span>{sub.name}</span>
                    {unread > 0 && (
                      <span style={{ background: '#F6E4E1', color: '#B23A3A', fontSize: '10px', fontWeight: 800, padding: '1px 6px', borderRadius: '12px', minWidth: 16, textAlign: 'center' }}>{unread}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          )}
          </>)}
        </div>

        <div className="ps-nav-group">
          {!collapsed && toolsNav.length > 0 && <div className="ps-nav-label">Settings & Tools</div>}
          {toolsNav.map((item) => (
            <NavLink 
              key={item.name} 
              to={item.path} 
              className={({ isActive }) => `ps-nav-item ${isActive ? 'active' : ''}`}
            >
              <span style={{ display: 'flex' }} className="ps-nav-icon">{item.icon}</span>
              <span style={{ flex: 1 }}>{item.name}</span>
              {item.badge === 'unreadOrders' && unreadOrders > 0 && !collapsed && (
                <span style={{ background: '#F6E4E1', color: '#B23A3A', fontSize: '11px', fontWeight: 'bold', padding: '2px 6px', borderRadius: '12px' }}>
                  {unreadOrders}
                </span>
              )}
            </NavLink>
          ))}
        </div>
      </div>

      <div className="ps-footer">
        <div className="ps-user-profile" style={{ justifyContent: collapsed ? 'center' : 'flex-start' }}>
          <div className="ps-user-avatar" style={{ background: '#111111', color: '#F9F4EE', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', flexShrink: 0 }}>
            {user.name.slice(0, 1).toUpperCase()}
          </div>

          {!collapsed && (
            <div className="ps-user-info" style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div className="ps-user-name" style={{ whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{user.name}</div>
              <div className="ps-user-role">{role}</div>
            </div>
          )}

          <button
            className="ps-logout-btn"
            onClick={() => setShowLogoutConfirm(true)}
            title="Log out"
            style={{ padding: '8px', borderRadius: '8px', border: 'none', background: 'transparent', cursor: 'pointer', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(178,58,58,0.12)'; e.currentTarget.style.color = '#B23A3A'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#94a3b8'; }}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </div>

      {showLogoutConfirm && (
        <div
          onClick={(e) => { if (e.target === e.currentTarget) setShowLogoutConfirm(false); }}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}
        >
          <div style={{ background: 'white', borderRadius: '16px', padding: '28px 32px', width: '400px', boxShadow: '0 20px 50px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#F6E4E1', color: '#B23A3A', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 800, color: '#0f172a' }}>Log out?</h3>
            </div>
            <p style={{ margin: '4px 0 20px 52px', fontSize: '13px', color: '#64748b' }}>
              You'll need to sign in again to access <strong>{workspaceName}</strong>.
            </p>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setShowLogoutConfirm(false)} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', color: '#0f172a', fontWeight: 700, cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleLogout} style={{ flex: 1, padding: '12px', borderRadius: '10px', border: 'none', background: '#B23A3A', color: '#F9F4EE', fontWeight: 800, cursor: 'pointer' }}>Log out</button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
};

export default Sidebar;
